import React, { useState, useEffect, useContext } from "react"
import { Link } from "gatsby"
import { AppContext } from "../../components/Context"
import { Helmet } from "react-helmet/es/Helmet"

import SEO from "../../components/SEO"
import Heading from "../../components/Heading"
import Image from "../../components/Image"
import AddToCartModal from "../../components/AddToCart"

import styles from "./favorites.module.scss"
import buttonStyles from "../../components/button.module.scss"

import favorites from "../../helpers/favorites"
import cart from "../../helpers/cart"
import { trackEvent, toTitleCase } from "../../helpers"

const Favorites = ({ location }) => {
  const [favoritesRetrieved, setFavoritesRetrieved] = useState(false)
  const [favoritesList, setFavoritesList] = useState([])

  useEffect(() => {
    favorites.getListProducts()
      .then(({ data }) => {
        // console.log(data)
        if (data.list)
          setFavoritesList(data.list)
        setFavoritesRetrieved(true)
      })
  }, [favoritesRetrieved])

  return(
    <>
      <SEO
        title="Your Favorites"
        description={`Page description here`}
        url={location.pathname}
      />

      <Heading
        tag="h1"
        level="0"
        className={styles.heading}
      >
        Your Favorites
      </Heading>
      <section className={styles.favorites}>
        {favoritesList.length > 0 ?
          <ul className={styles.grid}>
            {favoritesList.map((favorite, i) => (
              <Favorite
                key={i}
                favorite={favorite}
              />
            ))}
          </ul>
          :
          <div>
            <p>You don't have any favorites yet.</p>
            <Link
              to="/collections/all"
              className={buttonStyles.btnDark}
            >
              Start Favoriting Now
            </Link>
          </div>
        }
      </section>

      <Helmet>
        <script type="text/javascript">{`
          window.criteo_q = window.criteo_q || [];
          window.criteo_q.push(
            { event: "viewWishlist", ecpplugin: "shopify" }
          );
        `}</script>
      </Helmet>
    </>
  )
}

export default Favorites

const Favorite = ({ favorite }) => {
  const appState = useContext(AppContext)

  const [isFavorite, setIsFavorite] = useState(true)
  const [showSizeSelect, setShowSizeSelect] = useState(false)
  const [showAddToCartModal, setShowAddToCartModal] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState(favorite.variants[0])

  const handleToggleClick = () => {
    (!isFavorite) ? favorites.addItem(favorite.sku) : favorites.removeItem(favorite.sku)
    setIsFavorite(!isFavorite)
  }

  const addToCart = () => {
    const newItem = {
      variantId: selectedVariant.shopify_variant_id,
      quantity: 1,
      maxQuantity: selectedVariant.quantity,
      image: favorite.pdp[0],
      title: favorite.title,
      price: parseInt(favorite.price, 10).toLocaleString(),
      compareAtPrice: (favorite.compareAtPrice) ?
        parseInt(favorite.compareAtPrice, 10).toLocaleString()
        : null,
      url: `${favorite.url}?color=${favorite.color.name}`,
      color: toTitleCase(favorite.color.name),
      size: selectedVariant.size,
      dateExpected: selectedVariant.dateExpected,
    }

    cart.addItem(newItem)

    appState.set({
      cartCount: cart.getCartCount(),
    })

    setShowAddToCartModal(true)

    addToCartAnalytics()
  }

  const addToCartAnalytics = () => {
    // GA
    trackEvent("Wishlist", "Click", "Add to Cart")

    // Pinterest
    if (window.hasOwnProperty("pintrk"))
      window.pintrk("track", "addtocart", {
        value: (parseInt(selectedVariant.price, 10) / 100).toFixed(2),
        order_quantity: 1,
        currency: "USD",
        product_id: null,
      })

    // Custora
    if (window.hasOwnProperty("custoraTrack"))
      window.custoraTrack.addToCart(selectedVariant.shopify_variant_id, `$${favorite.price}.00`, 1, `$${favorite.compareAtPrice || "0"}.00`, selectedVariant.shopify_variant_id, favorite.title)

    // Heap
    if (window.hasOwnProperty("heap"))
      window.heap.track("Add to Cart")
  }

  return(
    <li
      className={isFavorite ? styles.active : styles.notActive}
    >
      <button
        className={isFavorite ? styles.toggleBtn : styles.toggleBtnAddBack}
        onClick={handleToggleClick}
      >
      </button>
      <Link
        to={favorite.url.replace("https://www.tamaramellon.com","")}
      >
        <Image
          image={favorite.pdp[0]}
          className={styles.image}
        />
      </Link>
      <Heading
        tag="h4"
        level="4"
        className={styles.productTitle}
      >
        <Link
          to={favorite.url.replace("https://www.tamaramellon.com","")}
        >
          {favorite.title}
        </Link>
      </Heading>
      <p
        className={styles.productPrice}
      >
        ${favorite.price}
      </p>
      <button
        className={buttonStyles.btnDarkOutline}
        onClick={() => setShowSizeSelect(!showSizeSelect)}
      >
        Add to Cart
      </button>

      {showSizeSelect &&
        <SizeSelect
          {...{
            favorite,
            setShowSizeSelect,
            setSelectedVariant,
            addToCart,
          }}
        />
      }

      {showAddToCartModal &&
        <AddToCartModal
          image={favorite.pdp[0]}
          title={favorite.title}
          color={toTitleCase(favorite.color.name)}
          size={selectedVariant.size}
          price={parseInt(favorite.price, 10).toLocaleString()}
          setShowAddToCartModal={setShowAddToCartModal}
        />
      }
    </li>
  )
}

const SizeSelect = props => {

  const {
    favorite,
    setShowSizeSelect,
    setSelectedVariant,
    addToCart,
  } = props

  const handleSizeClick = variant => {
    setShowSizeSelect(false)
    setSelectedVariant(variant)
    addToCart()
  }

  const handleOverlayClick = e => {
    if (e.target.className.includes("--overlay--"))
      setShowSizeSelect(false)
  }

  return(
    <>
      <div
        className={styles.overlay}
        role="presentation"
        onClick={handleOverlayClick}
      />
      <ul className={styles.sizesGrid}>
        {favorite.variants.map((variant, i) => (
          <li key={i}>
            {variant.inventory.available_to_sell ?
              <button
                onClick={() => handleSizeClick(variant)}
              >
                {variant.size} = {variant.size - 30} US
              </button>
              :
              <span>
                {variant.size} = {variant.size - 30} US
              </span>
            }
          </li>
        ))}
      </ul>
    </>
  )
}
